import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import type { FormSchemaV1, FormFieldDefinition, FormSectionDefinition, FormFieldKind } from "@rwaforms/shared";
import { validateFormSchema } from "@rwaforms/shared";
import type { BrandingTheme, FormDetail, FormDraftDetail } from "../types/api";
import { createField, createSection } from "../lib/field-factories";

type SidebarView = "properties" | "logic" | "responses" | "calculations" | "settings";

interface BuilderState {
  initialized: boolean;
  formId: string | null;
  teamId: string | null;
  draftId: string | null;
  draftVersionId: string | null;
  schema: FormSchemaV1;
  formTitle: string;
  formDescription: string | null;
  formStatus: "draft" | "published" | "archived";
  dirty: boolean;
  schemaVersion: number;  // Increments on every schema change
  isSaving: boolean;
  lastSavedAt: string | null;
  formTheme: BrandingTheme | null;
  teamTheme: BrandingTheme | null;
  selectedFieldId: string | null;
  selectedSectionId: string | null;
  sidebarView: SidebarView;
  loadFromDetail(detail: FormDetail): void;
  setFormTitle(title: string): void;
  setFormDescription(description: string): void;
  setDraftVersionId(versionId: string): void;
  setFormStatus(status: "draft" | "published" | "archived"): void;
  setSaving(value: boolean): void;
  markSaved(savedAtVersion?: number): void;
  selectField(fieldId: string | null): void;
  selectSection(sectionId: string | null): void;
  setSidebar(view: SidebarView): void;
  updateField(fieldId: string, updates: Partial<FormFieldDefinition>): void;
  updateSection(sectionId: string, updates: Partial<FormSectionDefinition>): void;
  addField(kind: FormFieldKind, sectionId?: string | null, index?: number): FormFieldDefinition | null;
  duplicateField(fieldId: string): FormFieldDefinition | null;
  removeField(fieldId: string): void;
  moveField(fieldId: string, targetSectionId: string | null, targetIndex: number): void;
  addSection(title?: string): FormSectionDefinition;
  removeSection(sectionId: string): void;
  loadSchema(schema: FormDraftDetail | null): void;
  updateSchema(schema: FormSchemaV1): void;
  applyRemoteUpdate(update: { schema: FormSchemaV1; versionId?: string | null; updatedAt?: string | null }): void;
  markDirty(): void;
  setFormTheme(theme: BrandingTheme | null): void;
  setTeamTheme(theme: BrandingTheme | null): void;
  updateFormSettings(settings: Partial<FormSchemaV1["settings"]>): void;
  getSchemaVersion(): number;
}

const EMPTY_SCHEMA: FormSchemaV1 = {
  version: "1.0",
  title: "",
  description: "",
  sections: [],
  fields: [],
  logicRules: [],
  settings: {},
  metadata: {},
};

export const useBuilderStore = create<BuilderState>()(
  immer((set, get) => ({
    initialized: false,
    formId: null,
    teamId: null,
    draftId: null,
    draftVersionId: null,
    schema: EMPTY_SCHEMA,
    formTitle: "",
    formDescription: "",
    formStatus: "draft",
    dirty: false,
    schemaVersion: 0,
    isSaving: false,
    lastSavedAt: null,
    formTheme: null,
    teamTheme: null,
    selectedFieldId: null,
    selectedSectionId: null,
    sidebarView: "properties",
    loadFromDetail(detail) {
      const draft = detail.draft ?? null;
      const currentState = get();
      // Only reset state if loading a different form or on initial load
      const isNewForm = !currentState.initialized || currentState.formId !== detail.form.id;

      set((state) => {
        state.initialized = true;
        state.formId = detail.form.id;
        state.teamId = detail.form.teamId;
        state.formTitle = detail.form.title;
        state.formDescription = detail.form.description ?? "";
        state.formStatus = detail.form.status;

        // Only overwrite version IDs, schema and reset dirty on initial load or form change
        // Preserve local state when refetching the same form to avoid overwriting
        // locally-updated draftVersionId with stale server data
        if (isNewForm) {
          state.draftId = draft?.id ?? detail.form.draftId ?? null;
          state.draftVersionId = draft?.versionId ?? detail.form.currentVersionId ?? null;
          state.schema = draft?.schema ?? detail.publishedVersion?.schema ?? EMPTY_SCHEMA;
          state.dirty = false;
          state.selectedFieldId = null;
          state.selectedSectionId = null;
        }
        // If not a new form, keep current draftVersionId, schema and dirty state

        state.lastSavedAt = draft?.updatedAt ?? detail.form.updatedAt ?? null;
        state.formTheme = detail.theme ?? null;
        state.teamTheme = detail.teamTheme ?? null;
      });
    },
    setFormTitle(title) {
      set((state) => {
        state.formTitle = title;
      });
    },
    setFormDescription(description) {
      set((state) => {
        state.formDescription = description;
      });
    },
    setDraftVersionId(versionId) {
      set((state) => {
        state.draftVersionId = versionId;
      });
    },
    setFormStatus(status) {
      set((state) => {
        state.formStatus = status;
      });
    },
    setSaving(value) {
      set((state) => {
        state.isSaving = value;
      });
    },
    markSaved(savedAtVersion?: number) {
      set((state) => {
        state.isSaving = false;
        // Only mark as saved if no changes were made since save started
        if (savedAtVersion === undefined || savedAtVersion === state.schemaVersion) {
          state.dirty = false;
        }
        state.lastSavedAt = new Date().toISOString();
      });
    },
    selectField(fieldId) {
      set((state) => {
        state.selectedFieldId = fieldId;
        if (fieldId) {
          state.sidebarView = "properties";
        }
      });
    },
    selectSection(sectionId) {
      set((state) => {
        state.selectedSectionId = sectionId;
        if (sectionId) {
          state.sidebarView = "properties";
        }
      });
    },
    setSidebar(view) {
      set((state) => {
        state.sidebarView = view;
      });
    },
    updateField(fieldId, updates) {
      set((state) => {
        const field = state.schema.fields.find((item) => item.id === fieldId);
        if (!field) return;
        Object.assign(field, updates);
        state.dirty = true;
        state.schemaVersion += 1;
      });
    },
    updateSection(sectionId, updates) {
      set((state) => {
        const section = state.schema.sections.find((item) => item.id === sectionId);
        if (!section) return;
        Object.assign(section, updates);
        reorderSections(state.schema.sections);
        state.dirty = true;
        state.schemaVersion += 1;
      });
    },
    addField(kind, sectionId = null, index) {
      let createdField: FormFieldDefinition | null = null;
      set((state) => {
        const position = nextFieldIndex(state.schema.fields, sectionId);
        createdField = createField(kind, position, sectionId);
        state.schema.fields.push(createdField);
        if (typeof index === "number") {
          moveFieldWithinSchema(state.schema.fields, createdField.id, sectionId, index);
        } else {
          normalizeSectionPositions(state.schema.fields, sectionId);
        }
        state.selectedFieldId = createdField.id;
        state.sidebarView = "properties";
        state.dirty = true;
        state.schemaVersion += 1;
      });
      return createdField;
    },
    duplicateField(fieldId) {
      const state = get();
      const existing = state.schema.fields.find((field) => field.id === fieldId);
      if (!existing) return null;
      const targetIndex = getFieldIndex(
        state.schema.fields,
        existing.sectionId ?? null,
        existing.id
      );
      const created = get().addField(
        existing.type,
        existing.sectionId ?? null,
        targetIndex + 1
      );
      if (!created) return null;
      get().updateField(created.id, {
        ...existing,
        id: created.id,
        position: created.position,
        title: `${existing.title} (copy)`,
      });
      get().markDirty();
      return get().schema.fields.find((field) => field.id === created.id) ?? null;
    },
    removeField(fieldId) {
      set((state) => {
        const field = state.schema.fields.find((item) => item.id === fieldId);
        if (!field) return;
        const sectionId = field.sectionId ?? null;
        state.schema.fields = state.schema.fields.filter((item) => item.id !== fieldId);
        normalizeSectionPositions(state.schema.fields, sectionId);
        if (state.selectedFieldId === fieldId) {
          state.selectedFieldId = null;
        }
        state.dirty = true;
        state.schemaVersion += 1;
      });
    },
    moveField(fieldId, targetSectionId, targetIndex) {
      set((state) => {
        moveFieldWithinSchema(state.schema.fields, fieldId, targetSectionId, targetIndex);
        state.schema.fields = [...state.schema.fields];
        const validation = validateFormSchema(state.schema);
        if (!validation.valid) {
          console.warn("[useBuilderStore] Schema validation issues after moveField:", validation.issues);
        }
        state.dirty = true;
        state.schemaVersion += 1;
      });
    },
    addSection(title = "Untitled section") {
      let section: FormSectionDefinition = createSection(title, get().schema.sections.length);
      set((state) => {
        state.schema.sections.push(section);
        reorderSections(state.schema.sections);
        state.selectedSectionId = section.id;
        state.sidebarView = "properties";
        state.dirty = true;
        state.schemaVersion += 1;
      });
      return section;
    },
    removeSection(sectionId) {
      set((state) => {
        state.schema.sections = state.schema.sections.filter((section) => section.id !== sectionId);
        state.schema.fields = state.schema.fields.map((field) =>
          field.sectionId === sectionId ? { ...field, sectionId: null } : field
        );
        normalizeSectionPositions(state.schema.fields, null);
        reorderSections(state.schema.sections);
        if (state.selectedSectionId === sectionId) {
          state.selectedSectionId = null;
        }
        state.dirty = true;
        state.schemaVersion += 1;
      });
    },
    loadSchema(draft) {
      const currentState = get();
      set((state) => {
        // Only overwrite schema if there are no pending local changes
        // This prevents save callbacks from erasing changes made during the save
        if (!currentState.dirty) {
          state.schema = draft?.schema ?? EMPTY_SCHEMA;
        }
        // Always update metadata
        state.dirty = false;
        state.draftId = draft?.id ?? state.draftId;
        state.draftVersionId = draft?.versionId ?? state.draftVersionId;
        state.lastSavedAt = draft?.updatedAt ?? state.lastSavedAt;
      });
    },
    updateSchema(schema) {
      set((state) => {
        // Validate schema before updating
        const validation = validateFormSchema(schema);
        if (!validation.valid) {
          console.error("[useBuilderStore] Invalid schema provided to updateSchema:", validation.issues);
          // Still update but log the issues
        }
        state.schema = schema;
        state.dirty = true;
      });
    },
    applyRemoteUpdate(update) {
      set((state) => {
        state.schema = update.schema;
        if (typeof update.versionId !== "undefined") {
          state.draftVersionId = update.versionId;
        }
        if (update.updatedAt) {
          state.lastSavedAt = update.updatedAt;
        }
        state.dirty = false;
      });
    },
    markDirty() {
      set((state) => {
        state.dirty = true;
        state.schemaVersion += 1;
      });
    },
    setFormTheme(theme) {
      set((state) => {
        state.formTheme = theme ?? null;
      });
    },
    setTeamTheme(theme) {
      set((state) => {
        state.teamTheme = theme ?? null;
      });
    },
    updateFormSettings(settings) {
      set((state) => {
        state.schema.settings = {
          ...state.schema.settings,
          ...settings,
        };
        state.dirty = true;
        state.schemaVersion += 1;
      });
    },
    getSchemaVersion() {
      return get().schemaVersion;
    },
  }))
);

function nextFieldIndex(fields: FormFieldDefinition[], sectionId: string | null): number {
  return fields.filter((field) => (field.sectionId ?? null) === sectionId).length;
}

function normalizeSectionPositions(fields: FormFieldDefinition[], sectionId: string | null): void {
  const sectionFields = fields
    .filter((field) => (field.sectionId ?? null) === sectionId)
    .sort((a, b) => a.position - b.position);
  sectionFields.forEach((field, index) => {
    field.position = index;
  });
}

function reorderSections(sections: FormSectionDefinition[]): void {
  sections.sort((a, b) => a.position - b.position);
  sections.forEach((section, index) => {
    section.position = index;
  });
}

/**
 * Moves a field within the schema using immutable updates with validation and integrity checks.
 * This function ensures data integrity and prevents race conditions.
 */
function moveFieldWithinSchema(
  fields: FormFieldDefinition[],
  fieldId: string,
  targetSectionId: string | null,
  targetIndex: number
): void {
  // Integrity check: Validate field exists
  const fieldIndex = fields.findIndex((item) => item.id === fieldId);
  if (fieldIndex === -1) {
    console.warn(`[moveFieldWithinSchema] Field ${fieldId} not found`);
    return;
  }

  // Integrity check: Validate target index is non-negative
  if (targetIndex < 0) {
    console.warn(`[moveFieldWithinSchema] Invalid target index: ${targetIndex}`);
    return;
  }

  const field = fields[fieldIndex];
  const previousSectionId = field.sectionId ?? null;

  // If field is already in the target position, no-op
  if (previousSectionId === targetSectionId) {
    const currentSectionFields = fields.filter(
      (item) => (item.sectionId ?? null) === targetSectionId
    );
    const currentIndex = currentSectionFields
      .sort((a, b) => a.position - b.position)
      .findIndex((item) => item.id === fieldId);
    if (currentIndex === targetIndex) {
      return; // Already in correct position
    }
  }

  // Create immutable copy of fields array
  const fieldsCopy = fields.map((f) => ({ ...f }));

  // Remove field from its current position
  const fieldToMove = { ...fieldsCopy[fieldIndex], sectionId: targetSectionId };
  const fieldsWithoutMoved = fieldsCopy.filter((item) => item.id !== fieldId);

  // Get fields in target section (sorted by position)
  const targetSectionFields = fieldsWithoutMoved
    .filter((item) => (item.sectionId ?? null) === targetSectionId)
    .sort((a, b) => a.position - b.position);

  // Validate target index is within bounds
  const maxIndex = targetSectionFields.length;
  const clampedTargetIndex = Math.min(Math.max(0, targetIndex), maxIndex);

  // Insert field at target position
  const updatedTargetFields = [
    ...targetSectionFields.slice(0, clampedTargetIndex),
    fieldToMove,
    ...targetSectionFields.slice(clampedTargetIndex),
  ];

  // Renumber positions in target section
  updatedTargetFields.forEach((item, index) => {
    item.position = index;
  });

  // Get fields from previous section (if different)
  const previousSectionFields =
    previousSectionId !== targetSectionId
      ? fieldsWithoutMoved
        .filter((item) => (item.sectionId ?? null) === previousSectionId)
        .sort((a, b) => a.position - b.position)
      : [];

  // Renumber positions in previous section
  previousSectionFields.forEach((item, index) => {
    item.position = index;
  });

  // Combine all fields: target section + previous section + other sections
  const otherFields = fieldsWithoutMoved.filter(
    (item) =>
      (item.sectionId ?? null) !== targetSectionId &&
      (item.sectionId ?? null) !== previousSectionId
  );

  // Build final array ensuring no duplicates
  const fieldIdSet = new Set<string>();
  const finalFields: FormFieldDefinition[] = [];

  // Add fields in order: target section, previous section, others
  for (const item of [...updatedTargetFields, ...previousSectionFields, ...otherFields]) {
    if (!fieldIdSet.has(item.id)) {
      fieldIdSet.add(item.id);
      finalFields.push(item);
    }
  }

  // Integrity check: Ensure all original fields are present
  const originalFieldIds = new Set(fields.map((f) => f.id));
  const finalFieldIds = new Set(finalFields.map((f) => f.id));
  const missingFieldIds = [...originalFieldIds].filter((id) => !finalFieldIds.has(id));

  if (missingFieldIds.length > 0) {
    console.error(`[moveFieldWithinSchema] Missing fields after move: ${missingFieldIds.join(", ")}`);
    // Add missing fields back
    for (const missingId of missingFieldIds) {
      const missingField = fields.find((f) => f.id === missingId);
      if (missingField) {
        finalFields.push({ ...missingField });
      }
    }
  }

  // Integrity check: Ensure no duplicate IDs
  const duplicateIds = finalFields
    .map((f) => f.id)
    .filter((id, index, arr) => arr.indexOf(id) !== index);
  if (duplicateIds.length > 0) {
    console.error(`[moveFieldWithinSchema] Duplicate field IDs detected: ${duplicateIds.join(", ")}`);
    // Remove duplicates, keeping first occurrence
    const seen = new Set<string>();
    const deduplicated: FormFieldDefinition[] = [];
    for (const item of finalFields) {
      if (!seen.has(item.id)) {
        seen.add(item.id);
        deduplicated.push(item);
      }
    }
    finalFields.splice(0, finalFields.length, ...deduplicated);
  }

  // Replace original array with immutable update
  fields.splice(0, fields.length, ...finalFields);

  // Final normalization to ensure positions are correct
  normalizeSectionPositions(fields, targetSectionId);
  if (previousSectionId !== targetSectionId) {
    normalizeSectionPositions(fields, previousSectionId);
  }
}

function getFieldIndex(
  fields: FormFieldDefinition[],
  sectionId: string | null,
  fieldId: string
): number {
  const sectionFields = fields
    .filter((field) => (field.sectionId ?? null) === sectionId)
    .sort((a, b) => a.position - b.position);
  return sectionFields.findIndex((field) => field.id === fieldId);
}
